'use client'

import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'

// Define consent state interface
interface ConsentState {
  essential: boolean
  preferences: boolean
  analytics: boolean
  advertising: boolean
}
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Slide from '@mui/material/Slide'
import { TransitionProps } from '@mui/material/transitions'
import Divider from '@mui/material/Divider'
import React from 'react'

// Animation for preferences dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

interface ConsentState {
  essential: boolean // Always true, can't be changed
  preferences: boolean
  analytics: boolean
  advertising: boolean
}

/**
 * Mobile-Optimized Cookie Consent Banner
 *
 * Features:
 * - Compact view on mobile (50-60px height) to improve engagement time
 * - Expandable to full view when user taps on mobile
 * - Always expanded on desktop (≥600px width)
 * - Smooth transitions and clear visual indicators
 * - Maintains accessibility and GDPR compliance
 */
export default function CookieConsentBanner() {
  // Initialize with false and determine based on stored consent in useEffect
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  // Track if consent has been checked to avoid flashing
  const [consentChecked, setConsentChecked] = useState(false)
  // Track if banner is in compact or expanded state for mobile
  const [isCompact, setIsCompact] = useState(true)

  // Make the preferences dialog function globally available
  useEffect(() => {
    if (typeof window !== 'undefined') {
      ;(window as any).showCookiePreferences = () => setShowPreferences(true)
    }
    return () => {
      if (typeof window !== 'undefined') {
        // Clean up when component unmounts
        ;(window as any).showCookiePreferences = () => {} // Replace with empty function instead of undefined
      }
    }
  }, [])

  // Handle window resize to reset compact state on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 600) {
        setIsCompact(false) // Always expanded on desktop
      } else if (showBanner) {
        setIsCompact(true) // Start compact on mobile only if banner is showing
      }
    }

    if (typeof window !== 'undefined') {
      handleResize() // Set initial state
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [showBanner])

  // Track if component is mounted to prevent hydration issues
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])
  const [consent, setConsent] = useState<ConsentState>({
    essential: true, // Essential cookies are always required
    preferences: false,
    analytics: false,
    advertising: false,
  })

  // Check for existing consent when component mounts
  useEffect(() => {
    const storedConsent = getStoredConsent()
    if (storedConsent) {
      // User already made choices, use them
      setConsent(storedConsent)

      // Also apply these choices to Google analytics immediately
      if (typeof window !== 'undefined' && (window as any).updateConsentState) {
        ;(window as any).updateConsentState(storedConsent)
      }

      // Don't show banner if consent is already stored
      setShowBanner(false)
    } else {
      // No stored consent, show the banner
      setShowBanner(true)
    }

    // Mark that we've checked for consent
    setConsentChecked(true)
  }, [])

  // Get stored consent from cookie
  const getStoredConsent = (): ConsentState | null => {
    if (typeof window === 'undefined') return null

    try {
      // More robust cookie parsing
      const cookies = document.cookie.split(';')
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=')
        if (name === 'cookie_preferences') {
          return JSON.parse(decodeURIComponent(value))
        }
      }
    } catch (error) {
      // If there's any error parsing (corrupted cookie, etc.), return null
      console.error('Error parsing cookie consent:', error)
      // Delete potentially corrupted cookie
      document.cookie = 'cookie_preferences=; max-age=-1; path=/; SameSite=Lax'
    }

    return null
  }

  // Save consent choices
  const saveConsent = (newConsent: ConsentState) => {
    // Update local state
    setConsent(newConsent)
    setShowBanner(false)
    setShowPreferences(false)
    setConsentChecked(true)

    // Update Google's consent state
    if (typeof window !== 'undefined' && (window as any).updateConsentState) {
      ;(window as any).updateConsentState(newConsent)
    }

    // Store the consent in a cookie
    // Set expiration to 1 year (365 days)
    const expirationDate = new Date()
    expirationDate.setFullYear(expirationDate.getFullYear() + 1)

    // Use secure cookie settings
    document.cookie =
      'cookie_preferences=' +
      encodeURIComponent(JSON.stringify(newConsent)) +
      '; expires=' +
      expirationDate.toUTCString() +
      '; path=/; SameSite=Lax' +
      (window.location.protocol === 'https:' ? '; Secure' : '')
  }

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      preferences: true,
      analytics: true,
      advertising: true,
    })
  }

  const handleAcceptNecessary = () => {
    saveConsent({
      essential: true,
      preferences: false,
      analytics: false,
      advertising: false,
    })
  }

  const handleSavePreferences = () => {
    saveConsent({
      ...consent,
      essential: true, // Always true
    })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConsent({
      ...consent,
      [event.target.name]: event.target.checked,
    })
  }

  // Show the preferences dialog
  const openPreferencesDialog = () => {
    setShowPreferences(true)
  }

  // This commented-out function could be used for a settings page
  // to allow users to re-open the banner if needed
  // const toggleBanner = () => {
  //   setShowBanner(prevState => !prevState);
  // };

  // Don't render anything until we've checked if consent exists AND component is mounted
  if (!consentChecked || !isMounted) {
    return null
  }

  // If banner and preferences are both hidden, don't render anything
  if (!showBanner && !showPreferences) {
    return null
  }

  return (
    <>
      {/* Main Cookie Banner */}
      {showBanner && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            p: isCompact ? { xs: 1, sm: 2 } : { xs: 1.5, sm: 2 },
            borderRadius: '8px 8px 0 0',
            backgroundColor: (theme) => theme.palette.background.default,
            boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
            // Compact height on mobile when collapsed - much smaller footprint
            minHeight: isCompact ? { xs: '50px', sm: 'auto' } : 'auto',
            maxHeight: isCompact ? { xs: '60px', sm: 'auto' } : 'auto',
            overflow: isCompact ? { xs: 'hidden', sm: 'visible' } : 'visible',
            cursor: isCompact ? { xs: 'pointer', sm: 'default' } : 'default',
            transition: 'all 0.3s ease-in-out',
          }}
          onClick={() => {
            // Only expand on mobile (xs breakpoint) and only if mounted
            if (isMounted && isCompact && window.innerWidth < 600) {
              setIsCompact(false)
            }
          }}
        >
          <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Show compact view only on mobile AND when isCompact is true */}
            {isCompact && (
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{
                        fontSize: '0.72rem',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                      }}
                    >
                      🍪 We use cookies. Tap for options
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '50%',
                        width: 16,
                        height: 16,
                        fontSize: '0.6rem',
                        justifyContent: 'center',
                        opacity: 0.8,
                      }}
                    >
                      ⌄
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAcceptAll()
                    }}
                    sx={{
                      minWidth: '70px',
                      fontSize: '0.65rem',
                      py: 0.4,
                      px: 1.2,
                      borderRadius: '16px',
                    }}
                    aria-label="Accept all cookies"
                  >
                    Accept
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Show expanded view when NOT compact OR on desktop */}
            {!isCompact && (
              <Box>
                {/* Close button for mobile expanded view only */}
                <Box
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Cookie Settings
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setIsCompact(true)}
                    sx={{
                      minWidth: 'auto',
                      p: 0.5,
                      borderRadius: '50%',
                      backgroundColor: 'action.hover',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                    }}
                    aria-label="Minimize cookie banner"
                  >
                    ✕
                  </Button>
                </Box>

                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
                >
                  Cookie Consent
                </Typography>
                <Typography variant="body2" component="p" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 2 }}>
                  We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our
                  traffic.{' '}
                  {isMounted && window.innerWidth >= 600
                    ? 'By clicking "Accept All", you consent to our use of cookies. Visit "Cookie Preferences" for more information or to adjust your preferences.'
                    : 'Choose your preferences below.'}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={openPreferencesDialog}
                    size="small"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.5, sm: 1 },
                      px: { xs: 1.5, sm: 2 },
                    }}
                    aria-label="Open cookie preferences dialog"
                  >
                    {isMounted && window.innerWidth >= 600 ? 'Cookie Preferences' : 'Manage Cookies'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleAcceptNecessary}
                    size="small"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.5, sm: 1 },
                      px: { xs: 1.5, sm: 2 },
                    }}
                    aria-label="Accept only necessary cookies"
                  >
                    {isMounted && window.innerWidth >= 600 ? 'Accept Necessary' : 'Necessary Only'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAcceptAll}
                    size="small"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.5, sm: 1 },
                      px: { xs: 1.5, sm: 2 },
                    }}
                    aria-label="Accept all cookies"
                  >
                    Accept All
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Cookie Preferences Dialog */}
      <Dialog
        open={showPreferences}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setShowPreferences(false)}
        aria-labelledby="cookie-preferences-dialog"
        aria-describedby="cookie-preferences-description"
        maxWidth="md"
      >
        <DialogTitle id="cookie-preferences-dialog">Cookie Preferences</DialogTitle>
        <DialogContent>
          <DialogContentText id="cookie-preferences-description" paragraph>
            Manage your cookie preferences. Necessary cookies help make a website usable by enabling basic functions.
          </DialogContentText>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={true} disabled name="essential" />}
              label={
                <Box>
                  <Typography variant="subtitle2" color="primary">
                    Essential Cookies (Always Active)
                  </Typography>
                  <Typography variant="caption" color="primary">
                    These cookies are necessary for the website to function and cannot be disabled.
                  </Typography>
                </Box>
              }
            />
            <Divider sx={{ my: 1.5 }} />

            <FormControlLabel
              control={<Checkbox checked={consent.preferences} onChange={handleChange} name="preferences" />}
              label={
                <Box>
                  <Typography variant="subtitle2">Preference Cookies</Typography>
                  <Typography variant="caption">
                    These cookies enable the website to remember your preferences and choices.
                  </Typography>
                </Box>
              }
            />
            <Divider sx={{ my: 1.5 }} />

            <FormControlLabel
              control={<Checkbox checked={consent.analytics} onChange={handleChange} name="analytics" />}
              label={
                <Box>
                  <Typography variant="subtitle2">Analytics Cookies</Typography>
                  <Typography variant="caption">
                    These cookies help us understand how visitors interact with the website, to improve performance and
                    user experience.
                  </Typography>
                </Box>
              }
            />
            <Divider sx={{ my: 1.5 }} />

            <FormControlLabel
              control={<Checkbox checked={consent.advertising} onChange={handleChange} name="advertising" />}
              label={
                <Box>
                  <Typography variant="subtitle2">Advertising Cookies</Typography>
                  <Typography variant="caption">
                    These cookies are used to deliver advertisements more relevant to you and your interests.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreferences(false)}>Cancel</Button>
          <Button onClick={handleSavePreferences} variant="contained">
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// Global interface for TypeScript is defined in @types/cookie-consent.d.ts
